/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { action, runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { faArrowDownToLine } from "@fortawesome/pro-light-svg-icons";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";

import { FontAwesomeSvgIcon } from "../../../FontAwesomeSvgIcon";
import { UtfButton } from "../../../inputs";
import Popover from "../../../Popover";
import Radio from "../../../Radio";

const XLSXFILETYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const CSVFILETYPE = "text/plain;charset=UTF-8";

const handleExport = {
  asXLSXFile: (data, filename, header) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data, { header });
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const dataBlob = new Blob([excelBuffer], { type: XLSXFILETYPE });
      FileSaver.saveAs(dataBlob, `${filename}.xlsx`);
    } catch (error) {
      console.error("XLSX export failed:", error);
    }
  },
  asCSVFile: (data, filename, downloadOption, header) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data, { header });
      const range = XLSX.utils.decode_range(ws["!ref"]);

      // Process numeric cells based on separator option
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
          if (!cell || cell.t !== "n") continue;
          cell.w = cell.v
            .toString(10)
            .replace(/\./, downloadOption === "CSV_WITH_DOT_SEPARATOR" ? "." : ",");
        }
      }

      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: CSVFILETYPE });
      FileSaver.saveAs(blob, `${filename}.CSV`);
    } catch (error) {
      console.error("CSV export failed:", error);
    }
  },
};

function formatDateForExport(value: unknown): string {
  const date = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) return String(value ?? "");
  return `${date.toLocaleDateString("sv-SE")} ${date.toLocaleTimeString("sv-SE")}`;
}
function isDateColumn(column: { columnDef?: { type?: string } }): boolean {
  return column.columnDef?.type === "date";
}

function ExportButton({ table, filename, translationNs = "_common", disabled = false }) {
  const { t } = useTranslation([...translationNs, "_action"]);

  const downloadOptions = useMemo(
    () => ({
      CSV_WITH_DOT_SEPARATOR: {
        label: t("action_download_csv", { ns: "_action" }),
        info: t("action_download_csv_dot_info", { ns: "_action" }),
      },
      CSV_WITH_COMMA_SEPARATOR: {
        label: t("action_download_csv", { ns: "_action" }),
        info: t("action_download_csv_comma_info", { ns: "_action" }),
      },
      XLSX: { label: t("action_download_xlsx", { ns: "_action" }), info: "" },
    }),
    [t]
  );

  const local = useLocalObservable(() => ({
    openPopover: null,
    downloadOption: null,

    //* Action
    handleDownloadOptionChange: action((event) => {
      local.downloadOption = event.target.value;
    }),

    togglePopoverOpen: action((event) => {
      local.openPopover = event.currentTarget;
    }),

    togglePopoverClose: action(() => {
      local.openPopover = null;
      local.downloadOption = null;
    }),

    getTableRowsAndHeaders: action(() => {
      // Validate table object
      if (!table) {
        console.error("Table object is null or undefined");
        return { rows: [], headerLabels: [] };
      }

      try {
        // Get columns
        let visibleColumns;
        if (typeof table.getVisibleLeafColumns === "function") {
          visibleColumns = table.getVisibleLeafColumns();
        } else if (typeof table.getAllLeafColumns === "function") {
          visibleColumns = table
            .getAllLeafColumns()
            .filter((col) => (typeof col.getIsVisible === "function" ? col.getIsVisible() : true));
        } else if (typeof table.getAllColumns === "function") {
          visibleColumns = table
            .getAllColumns()
            .filter((col) => (typeof col.getIsVisible === "function" ? col.getIsVisible() : true));
        } else {
          throw new Error("No suitable column getter method found on table object");
        }

        // Filter out MRT-internal display columns (expand handle, row-select checkbox)
        // they have no exportable value and would render as empty `mrt-row-*` columns.
        const filteredColumns = visibleColumns.filter(
          ({ id }) => id !== "mrt-row-expand" && id !== "mrt-row-select"
        );

        // Get rows
        let tableRows;
        if (typeof table.getPrePaginationRowModel === "function") {
          tableRows = table.getPrePaginationRowModel().rows;
        } else if (typeof table.getRowModel === "function") {
          tableRows = table.getRowModel().rows;
        } else if (table.rows) {
          tableRows = table.rows;
        } else {
          throw new Error("No suitable row getter method found on table object");
        }

        if (tableRows.length === 0) {
          console.warn("No rows to export");
          return { rows: [], headerLabels: [] };
        }

        // Process headers
        const headers = {};
        filteredColumns.forEach((column) => {
          const { id, parent, columnDef } = column;
          const { header, sublabel } = columnDef || {};

          let headerText;
          try {
            const pHeader = parent ? t(parent.columnDef?.header || "", { ns: translationNs }) : "";
            const label = t(header || id, { ns: translationNs });
            const subLabel = sublabel ? t(sublabel, { ns: translationNs }) : "";

            if (subLabel) {
              headerText = `${label} - ${subLabel}`;
            } else if (parent && pHeader) {
              headerText = `${pHeader} - ${label}`;
            } else {
              headerText = label;
            }
          } catch (translationError) {
            console.warn("Translation error for header:", id, translationError);
            headerText = header || id;
          }

          headers[id] = headerText;
        });

        // Process rows
        const processedRows = [];
        tableRows.forEach((tableRow, rowIndex) => {
          try {
            const row = {};

            filteredColumns.forEach((column) => {
              const { id } = column;
              const headerLabel = headers[id];

              if (!headerLabel) {
                console.warn(`No header label for column: ${id}`);
                return;
              }

              let cellValue;

              try {
                if (typeof tableRow.getValue === "function") {
                  cellValue = tableRow.getValue(id);
                } else if (
                  typeof tableRow.original === "object" &&
                  tableRow.original[id] !== undefined
                ) {
                  cellValue = tableRow.original[id];
                } else if (tableRow[id] !== undefined) {
                  cellValue = tableRow[id];
                } else {
                  console.warn(`Could not get value for column ${id} in row ${rowIndex}`);
                  cellValue = "";
                }
              } catch (valueError) {
                console.warn(
                  `Error getting value for column ${id} in row ${rowIndex}:`,
                  valueError
                );
                cellValue = "";
              }

              // Process the cell value
              let processedValue = cellValue;

              if (cellValue === null || cellValue === undefined) {
                processedValue = "";
              } else if (isDateColumn(column)) {
                processedValue = formatDateForExport(cellValue);
              } else if (typeof cellValue === "object") {
                if (Array.isArray(cellValue)) {
                  processedValue = cellValue.join(", ");
                } else if (cellValue.props) {
                  // Handle React components
                  if (cellValue.props.title) {
                    processedValue = "📝"; // FD Note col temporary emoji
                  } else {
                    processedValue = "⚑"; // FD Flag col
                  }
                } else if (cellValue.toFormat && typeof cellValue.toFormat === "function") {
                  // Handle DateTime objects
                  try {
                    processedValue = cellValue.toFormat("dd MMM yyyy HH:mm");
                  } catch (dateError) {
                    console.warn("Date formatting error:", dateError);
                    processedValue = String(cellValue);
                  }
                } else {
                  processedValue = String(cellValue);
                }
              } else if (typeof cellValue === "function") {
                console.warn(`Function value detected in column ${id}, row ${rowIndex}`);
                processedValue = "[Function]";
              } else {
                processedValue = cellValue;
              }

              row[headerLabel] = processedValue;
            });

            if (Object.keys(row).length > 0) {
              processedRows.push(row);
            }
          } catch (rowError) {
            console.error(`Error processing row ${rowIndex}:`, rowError);
          }
        });

        const headerLabels = filteredColumns.map(({ id }) => headers[id]).filter(Boolean);
        return { rows: processedRows, headerLabels };
      } catch (error) {
        console.error("Error in getTableRowsAndHeaders:", error);
        return { rows: [], headerLabels: [] };
      }
    }),
  }));

  const handleExportFile = () => {
    try {
      runInAction(() => {
        const { rows, headerLabels } = local.getTableRowsAndHeaders();

        if (rows.length === 0) {
          console.warn("No data to export");
          return;
        }

        if (local.downloadOption === "XLSX") {
          handleExport.asXLSXFile(rows, filename, headerLabels);
        } else {
          handleExport.asCSVFile(rows, filename, local.downloadOption, headerLabels);
        }

        local.togglePopoverClose();
      });
    } catch (error) {
      console.error("Export process failed:", error);
    }
  };

  // Add validation for required props
  if (!table) {
    console.error("ExportButton: table prop is required");
  }

  return (
    <>
      <Tooltip arrow title={t("text_export_list")}>
        <IconButton
          color={local.openPopover ? "secondary" : "primary"}
          onClick={disabled ? undefined : local.togglePopoverOpen}
          data-testid="export-list-button"
          size="large"
        >
          <FontAwesomeSvgIcon icon={faArrowDownToLine} />
        </IconButton>
      </Tooltip>
      <Popover open={local.openPopover} handleClose={local.togglePopoverClose}>
        <div>
          <Box
            component={Typography}
            variant="h5"
            padding={3}
            boxShadow={1}
            textTransform="uppercase"
          >
            {t("text_available_exports")}
          </Box>
          <FormControl fullWidth>
            <RadioGroup value={local.downloadOption} onChange={local.handleDownloadOptionChange}>
              {Object.entries(downloadOptions).map(([optionId, option]) => (
                <Fragment key={optionId}>
                  <FormControlLabel
                    value={optionId}
                    data-testid={optionId}
                    control={
                      <Radio
                        sx={{
                          marginRight: 1,
                        }}
                      />
                    }
                    label={
                      <>
                        <Typography variant="caption">{option.label}</Typography>
                        <Typography fontSize={12}>{option.info}</Typography>
                      </>
                    }
                    sx={{
                      margin: 0,
                      flexDirection: "row-reverse",
                      justifyContent: "flex-end",
                      ".MuiFormControlLabel-label": {
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        width: "100%",
                        mr: 5,
                        p: 2,
                        pl: 3,
                      },
                    }}
                  />
                  <Divider />
                </Fragment>
              ))}
            </RadioGroup>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: [2, 3],
            }}
          >
            <UtfButton
              color="primary"
              variant="contained"
              disabled={local.downloadOption === null}
              onClick={handleExportFile}
              data-testid="confirm-export-button"
            >
              {t("action_confirm", { ns: "_action" })}
            </UtfButton>
            <UtfButton
              color="primary"
              variant="outlined"
              onClick={local.togglePopoverClose}
              data-testid="cancel-export-button"
            >
              {t("action_cancel", { ns: "_action" })}
            </UtfButton>
          </Box>
        </div>
      </Popover>
    </>
  );
}

export default observer(ExportButton);
